/* eslint-disable @typescript-eslint/no-use-before-define */
import { Item } from '@/api';

import React, { useMemo, useRef, useState } from 'react';
import { useMeta } from '@/hooks/api/useMeta';
import { MetaAvailableResponse } from '@/queries/search';
import i18next from 'i18n';
import { HoverPopover, SortMenu, filterChipClass, filterPanelClass, useOverflows } from '@/components/design-system/Filters';
import DSIcon from '@/components/design-system/Icon';
import { SearchInput, SelectionRow } from '@/components/design-system/Input';
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    PopoverButton,
    PopoverGroup,
    PopoverPanel
} from '@headlessui/react';
import uniq from 'lodash.uniq';
import AlignCenter from 'public/assets/icons/align-center.svg';
import { useMobile } from '@/hooks/useMobile';
import clsx from 'clsx';
import { DisplaySortFields, DisplaySortOptions, DisplaySortOptionsType, findSortField } from '@/types/API';
import { analytics } from '@/helpers/Analytics';

export type DefaultFilters = { subcategories?: string[], categories?: string[], genres?: string[], artists?: string[], offerTypes?: string[], conditions?: string[], partners?: string[], collections?: string[] };

type ISearchFiltersProps = {
    defaultFilters?: DefaultFilters;
    isDefaultFilterFixed?: boolean;
    artistsFilters: string[];
    offerTypeFilters: string[];
    subCategoriesFilters: string[];
    conditionsFilters: string[];
    partnersFilters: string[];
    collectionsFilters: string[];
    genresFilters: string[];
    categoriesFilters: string[];
    sortBy?: DisplaySortOptionsType | null;
    /** The page's resting order — what the x puts back, and what leaves the chip reading "Sort". */
    defaultSortBy?: DisplaySortOptionsType | null;
    meta_available: MetaAvailableResponse;
    setArtistsFilters: (filter: string[]) => void;
    setOfferTypeFilters: (filter: string[]) => void;
    setConditionsFilters: (filter: string[]) => void;
    setPartnersFilters: (filter: string[]) => void;
    setCollectionsFilters: (filter: string[]) => void;
    setGenresFilters: (filter: string[]) => void;
    setCategoriesFilters: (filter: string[]) => void;
    setSubCategoriesFilters: (filter: string[]) => void;
    setSortBy: (sort: DisplaySortOptionsType | null) => void;
    loading: boolean;
};

type FilterEntity = {
    id: string;
    name: string;
    count: number;
    disabled?: boolean;
    /** Clears just this filter. Pinned defaults survive it. */
    onClear: () => void;
    options: { value: string, label: string, selected: boolean, disabled?: boolean, onChange: (val: boolean) => void }[]
};

const SearchFilters = (props: ISearchFiltersProps) => {
    const {
        offerTypeFilters,
        conditionsFilters,
        collectionsFilters,
        genresFilters,
        categoriesFilters,
        subCategoriesFilters,
        defaultFilters,
        meta_available: metaAvailable,
        sortBy,
        defaultSortBy,
        setCategoriesFilters,
        setConditionsFilters,
        setSubCategoriesFilters,
        setCollectionsFilters,
        setGenresFilters,
        setOfferTypeFilters,
        setSortBy,
        isDefaultFilterFixed = false
    } = props;
    const { categories, genres, conditions, subCategories, collections, offer_types: offerTypes } = useMeta(metaAvailable);
    const [open, setOpen] = useState(false);

    const isMobile = useMobile();

    const filters = useMemo(() => {
        const fa: FilterEntity[] = [];
        /** fa.push({
            id: 'partner',
            count: partnersFilters.length,
            disabled: !(partners && partners.length),
            name: i18next.t('header:search-filters.partner'),
            options: (partners || []).map((partner) => {
                const disabled = defaultFilters?.partners?.includes(partner.name) && isDefaultFilterFixed;
                return {
                    value: partner.name,
                    label: partner.name,
                    disabled,
                    selected: partnersFilters.includes(partner.name),
                    onChange: (checked: boolean) => {
                        analytics.track('Filter', { type: 'Partner', mode: checked ? 'set' : 'unset', value: partner.name });
                        if (checked) {
                            setPartnersFilters(uniq([...partnersFilters, partner.name]));
                        } else {
                            setPartnersFilters(partnersFilters.filter((partnerStr: string) => partnerStr !== partner.name));
                        }
                    }
                };
            })
        });

        fa.push({
            id: 'artists',
            onClear: () => setArtistsFilters(isDefaultFilterFixed ? (defaultFilters?.artists ?? []) : []),
            count: artistsFilters.length,
            disabled: !(artists && artists.length),
            name: i18next.t('header:search-filters.artists'),
            options: (artists || []).map((artist: ItemArtist) => {
                const disabled = defaultFilters?.artists?.includes(artist.slug) && isDefaultFilterFixed;
                return {
                    value: artist.slug,
                    label: artist.name,
                    disabled,
                    selected: artistsFilters.includes(artist.slug),
                    onChange: (checked: boolean) => {
                        analytics.track('Filter', { type: 'Artist', mode: checked ? 'set' : 'unset', value: artist.slug });
                        if (checked) {
                            setArtistsFilters(uniq([...artistsFilters, artist.slug]));
                        } else {
                            setArtistsFilters(artistsFilters.filter((artistSlug: string) => artistSlug !== artist.slug));
                        }
                    }
                };
            })
        });* */

        /*
         * Offer type was three toggle buttons sitting in the middle of the
         * results toolbar. It is a filter like any other, so it is a
         * dropdown now — same panel, same count badge, same clear.
         *
         * A type with no matching items is disabled, but only while it is
         * NOT selected, so a selection that has gone empty can still be
         * cleared. The analytics event keeps its old OfferTypeTop name so
         * existing reporting still lines up.
         */
        fa.push({
            id: 'type',
            onClear: () => setOfferTypeFilters(isDefaultFilterFixed ? (defaultFilters?.offerTypes ?? []) : []),
            count: offerTypeFilters.length,
            disabled: !(offerTypes && offerTypes.length),
            name: i18next.t('header:search-filters.offer-type'),
            options: Object.values(Item.OfferTypeEnum)
                .filter((type) => type !== Item.OfferTypeEnum.OfferInstant)
                .map((value) => {
                    const selected = offerTypeFilters.includes(value as string);
                    const unavailable = !offerTypes.find((type) => type.value === value);
                    const fixed = defaultFilters?.offerTypes?.includes(value as string) && isDefaultFilterFixed;
                    return {
                        value: value as string,
                        label: i18next.t(`offer-types.${value}` as 'offer-types.auction'),
                        disabled: (fixed || unavailable) && !selected,
                        selected,
                        onChange: (checked: boolean) => {
                            analytics.track('Filter', { type: 'OfferTypeTop', mode: checked ? 'set' : 'unset', value });
                            if (checked) {
                                setOfferTypeFilters(uniq([...offerTypeFilters, value as string]));
                            } else {
                                setOfferTypeFilters(offerTypeFilters.filter((name: string) => name !== value));
                            }
                        }
                    };
                })
        });

        fa.push({
            id: 'condition',
            onClear: () => setConditionsFilters(isDefaultFilterFixed ? (defaultFilters?.conditions ?? []) : []),
            count: conditionsFilters.length,
            disabled: !(conditions && conditions.length),
            name: i18next.t('header:search-filters.condition'),
            options: (conditions || []).map((condition) => {
                const disabled = defaultFilters?.conditions?.includes(condition.name) && isDefaultFilterFixed;
                return {
                    value: condition.name,
                    label: condition.name,
                    disabled,
                    selected: conditionsFilters.includes(condition.name),
                    onChange: (checked: boolean) => {
                        analytics.track('Filter', { type: 'Condition', mode: checked ? 'set' : 'unset', value: condition.name });
                        if (checked) {
                            setConditionsFilters(uniq([...conditionsFilters, condition.name]));
                        } else {
                            setConditionsFilters(conditionsFilters.filter((conditionStr: string) => conditionStr !== condition.name));
                        }
                    }
                };
            })
        });

        fa.push({
            id: 'genres',
            onClear: () => setGenresFilters(isDefaultFilterFixed ? (defaultFilters?.genres ?? []) : []),
            count: genresFilters.length,
            disabled: !(genres && genres.length),
            name: i18next.t('header:search-filters.genres'),
            options: (genres || []).map((genre) => {
                const disabled = defaultFilters?.genres?.includes(genre.slug) && isDefaultFilterFixed;
                return {
                    value: genre.slug,
                    label: genre.name,
                    disabled,
                    selected: genresFilters.includes(genre.slug),
                    onChange: (checked: boolean) => {
                        analytics.track('Filter', { type: 'Genre', mode: checked ? 'set' : 'unset', value: genre.slug });
                        if (checked) {
                            setGenresFilters(uniq([...genresFilters, genre.slug]));
                        } else {
                            setGenresFilters(genresFilters.filter((genreStr: string) => genreStr !== genre.slug));
                        }
                    }
                };
            })
        });

        fa.push({
            id: 'categories',
            onClear: () => setCategoriesFilters(isDefaultFilterFixed ? (defaultFilters?.categories ?? []) : []),
            count: categoriesFilters.length,
            disabled: !(categories && categories.length),
            name: i18next.t('header:search-filters.categories'),
            options: (categories || []).map((category) => {
                const disabled = defaultFilters?.categories?.includes(category.slug) && isDefaultFilterFixed;
                return {
                    value: category.slug,
                    label: category.name,
                    disabled,
                    selected: categoriesFilters.includes(category.slug),
                    onChange: (checked: boolean) => {
                        analytics.track('Filter', { type: 'Category', mode: checked ? 'set' : 'unset', value: category.slug });
                        if (checked) {
                            setCategoriesFilters(uniq([...categoriesFilters, category.slug]));
                        } else {
                            setCategoriesFilters(categoriesFilters.filter((catSlug: string) => catSlug !== category.slug));
                        }
                    }
                };
            })
        });

        fa.push({
            id: 'sub-categories',
            onClear: () => setSubCategoriesFilters(isDefaultFilterFixed ? (defaultFilters?.subcategories ?? []) : []),
            count: subCategoriesFilters.length,
            disabled: !(subCategories && subCategories.length),
            name: i18next.t('header:search-filters.sub-categories'),
            options: (subCategories || []).map((subCategory) => {
                const disabled = defaultFilters?.subcategories?.includes(subCategory.slug) && isDefaultFilterFixed;
                return {
                    value: subCategory.slug,
                    label: subCategory.name,
                    disabled,
                    selected: subCategoriesFilters.includes(subCategory.slug),
                    onChange: (checked: boolean) => {
                        if (checked) {
                            analytics.track('Filter', { type: 'SubCategory', value: subCategory.slug });
                            setSubCategoriesFilters(uniq([...subCategoriesFilters, subCategory.slug]));
                        } else {
                            setSubCategoriesFilters(subCategoriesFilters.filter((catSlug: string) => catSlug !== subCategory.slug));
                        }
                    }
                };
            })
        });

        fa.push({
            id: 'collections',
            onClear: () => setCollectionsFilters(isDefaultFilterFixed ? (defaultFilters?.collections ?? []) : []),
            count: collectionsFilters.length,
            disabled: !(collections && collections.length),
            name: i18next.t('header:search-filters.collection'),
            options: (collections || []).map((collection) => {
                const disabled = defaultFilters?.collections?.includes(collection.slug) && isDefaultFilterFixed;
                return {
                    value: collection.slug,
                    label: collection.name,
                    disabled,
                    selected: collectionsFilters.includes(collection.slug),
                    onChange: (checked: boolean) => {
                        analytics.track('Filter', { type: 'Collection', mode: checked ? 'set' : 'unset', value: collection.slug });
                        if (checked) {
                            setCollectionsFilters(uniq([...collectionsFilters, collection.slug]));
                        } else {
                            setCollectionsFilters(collectionsFilters.filter((collectionStr: string) => collectionStr !== collection.slug));
                        }
                    }
                };
            })
        });

        return fa;
    }, [categories, categoriesFilters, collections,
        collectionsFilters, conditions, conditionsFilters,
        subCategoriesFilters, defaultFilters?.subcategories,
        defaultFilters?.categories, defaultFilters?.collections, defaultFilters?.conditions,
        defaultFilters?.genres, genres, genresFilters, isDefaultFilterFixed,
        setSubCategoriesFilters, subCategories,
        setCategoriesFilters, setCollectionsFilters, setConditionsFilters, setGenresFilters,
        // The type section reads these; without them the memo would not
        // recompute on selection, so the row's checkbox and the count badge
        // would both go stale.
        defaultFilters?.offerTypes, offerTypeFilters, offerTypes, setOfferTypeFilters]);

    /* Which row is lit, and which way it points. */
    const activeSort = useMemo(() => findSortField(sortBy?.value), [sortBy]);
    const defaultSort = useMemo(() => findSortField(defaultSortBy?.value), [defaultSortBy]);

    const filtersCounter = (genresFilters.length + conditionsFilters.length + categoriesFilters.length + subCategoriesFilters.length + collectionsFilters.length + offerTypeFilters.length);

    /*
     * Clear-all runs each section's own clear rather than resetting the state
     * itself, so the two can never disagree about what "cleared" means — and a
     * pinned default stays pinned, because that rule lives in one place.
     */
    const clearAllFilters = () => filters.forEach((section) => section.onClear());

    return (
        <div>
            <Dialog open={isMobile && open} onClose={setOpen} className="relative z-40 md:hidden">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
                />
                <div className="fixed inset-0 top-[var(--header-h,68px)] z-40 flex">
                    <DialogPanel
                        transition
                        className="relative ml-auto flex h-full w-full max-w-xs transform flex-col overflow-y-auto bg-surface-000 py-[16px] pb-[24px] shadow-ds-big transition duration-300 ease-in-out data-[closed]:translate-x-full"
                    >
                        <div className="flex items-center justify-between px-[16px]">
                            <h2 className="text-h2 text-ink-400">{i18next.t('header:search-filters.title')}{filtersCounter > 0 ? ` (${filtersCounter})` : ''}</h2>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label={i18next.t('common:offers.close')}
                                className="flex size-[40px] shrink-0 items-center justify-center text-ink-400 transition-colors duration-press ease-ds-out hover:bg-surface-200 focus-visible:outline focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-accent-blue"
                            >
                                <DSIcon name="close" size={20} />
                            </button>
                        </div>

                        <form className="mt-[16px]">
                            {filters.map((section, i) => (
                                <Disclosure key={section.name} as="div" className={clsx('px-[16px] py-[24px]', { 'border-t border-surface-300': i > 0 })}>
                                    <h3 className="-mx-2 -my-3 flow-root">
                                        <DisclosureButton disabled={section.disabled} className="text-body-400 group flex w-full items-center justify-between px-[8px] py-[12px] text-ink-300 disabled:opacity-40">
                                            <span className="text-ink-400">{section.name} {section.count > 0 ? ` (${section.count})` : ''}</span>
                                            <span className="ml-6 flex items-center">
                                                <DSIcon
                                                    name="chevron-down"
                                                    size={20}
                                                    className="rotate-0 transform transition-transform duration-dropdown ease-ds-out group-data-[open]:-rotate-180"
                                                />
                                            </span>
                                        </DisclosureButton>
                                    </h3>
                                    <DisclosurePanel className="pt-[24px]">
                                        <div className="space-y-[24px]">
                                            <FilterSection section={section} isMobile={isMobile} />
                                        </div>
                                    </DisclosurePanel>
                                </Disclosure>
                            ))}
                        </form>
                    </DialogPanel>
                </div>
            </Dialog>
            {/*
              * Results toolbar — TR - Page Designs [MASTER] 261:10433.
              *
              * The comp puts the dropdowns and Sort in one right-aligned row,
              * always visible, with the term and result count on the left
              * (rendered by search.tsx). Previously the chips were collapsed
              * to height 0 behind a "Filter" toggle and the row carried three
              * offer-type buttons in the middle; the toggle now only opens the
              * mobile sheet, so it is mobile-only.
              *
              * Chips come from the DS (filterChipClass, 33px). The page comp
              * draws them 40px — a DS-vs-page discrepancy left as the DS's,
              * since forking the chip here is what caused the last drift.
              */}
            <div>
                <section aria-labelledby="filter-heading" className="py-[24px]">
                    {/*
                      * Mobile is a two-column GRID, not a flex row. Sort and
                      * Filter are the only two controls there and they are
                      * meant to sit at exactly half each; two flex items
                      * growing from a zero basis should have come to the same
                      * thing, but measured on the page they did not, and a
                      * grid track is half the row by construction rather than
                      * by whatever the items' intrinsic widths negotiate. The
                      * hidden chip group and headlessui's focus sentinel are
                      * display:none, so they take no track.
                      *
                      * Desktop is the flex row: every control is `flex-auto
                      * shrink-0`, growing from its own content width so only
                      * the SLACK is shared out. That fills the row edge to
                      * edge without sizing "Type" and "Sub Categories" alike,
                      * and refusing to shrink means a long name wraps to a
                      * second line rather than being cut in half.
                      */}
                    <div className="grid w-full grid-cols-2 gap-[4px] md:flex md:flex-wrap md:items-center">
                        {/* HoverPopover: opens on hover on desktop. The click
                            path is untouched, and on touch it is the only one —
                            there the chips open the filter panel instead. */}
                        <PopoverGroup className="hidden md:contents">
                            {/*
                              * Clear-all leads the bar, per master 255:2264, and
                              * only once something is on. A count badge tells you
                              * a filter is set; this is how you take it off
                              * without opening the panel to untick rows.
                              */}
                            {filtersCounter > 0 && (
                                <button
                                    type="button"
                                    onClick={clearAllFilters}
                                    aria-label={i18next.t('common:general.clear-all')}
                                    className="inline-flex size-[33px] shrink-0 items-center justify-center text-ink-400 transition-colors hover:bg-surface-200"
                                >
                                    <DSIcon name="close" size={20} />
                                </button>
                            )}
                            {filters.map((section) => (
                                <HoverPopover
                                    key={section.name}
                                    id={`desktop-menu-${section.id}`}
                                    className="relative flex-auto shrink-0 text-left"
                                >
                                    <span className={clsx(filterChipClass({ disabled: section.disabled }), 'flex w-full !px-0', section.count > 0 && 'pr-[16px]')}>
                                        <PopoverButton
                                            disabled={section.disabled}
                                            className={clsx(
                                                'inline-flex h-full min-w-0 flex-1 items-center justify-between gap-[8px] pl-[16px] disabled:cursor-not-allowed',
                                                section.count > 0 ? 'pr-[8px]' : 'pr-[16px]'
                                            )}
                                        >
                                            <span className="truncate">{section.name}</span>
                                            {section.count > 0 ? (
                                                <span className="text-body-600 inline-flex size-[18px] items-center justify-center bg-surface-300 tabular-nums text-ink-400">
                                                    {section.count}
                                                </span>
                                            ) : (
                                                <DSIcon name="chevron-down" size={16} className="shrink-0" />
                                            )}
                                        </PopoverButton>

                                        {section.count > 0 && (
                                            <button
                                                type="button"
                                                onClick={section.onClear}
                                                aria-label={`${i18next.t('common:general.clear')} ${section.name}`}
                                                className="inline-flex items-center text-ink-400"
                                            >
                                                <DSIcon name="close" size={16} />
                                            </button>
                                        )}
                                    </span>
                                    {/*
                                      * `anchor` rather than absolute right-0:
                                      * headlessui positions it through
                                      * floating-ui, which shifts the panel back
                                      * inside the viewport instead of letting
                                      * it hang off. Right-aligned, the leftmost
                                      * chip's panel ran off the left edge —
                                      * Type's was cut in half.
                                      */}
                                    <PopoverPanel
                                        transition
                                        data-no-focus-ring
                                        anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
                                        className={clsx(
                                            'z-20 origin-top-left transition focus:outline-none',
                                            '!overflow-visible [--anchor-max-height:none]',
                                            'data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
                                            'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
                                            filterPanelClass
                                        )}
                                    >
                                        <FilterSection section={section} />
                                    </PopoverPanel>
                                </HoverPopover>
                            ))}
                        </PopoverGroup>

                        {/* Shared DS control — see SortMenu. One row per field
                            rather than per API value, so Name is a row you
                            turn around rather than two rows to choose between,
                            and the trigger carries the answer. */}
                        <SortMenu
                            className="md:flex-auto md:shrink-0"
                            label={i18next.t('header:search-sort.sort')}
                            options={DisplaySortFields.map((field) => ({
                                id: field.id,
                                label: i18next.t(field.label),
                                icon: field.icon,
                                reversible: Boolean(field.desc)
                            }))}
                            activeId={activeSort?.field.id}
                            defaultId={defaultSort?.field.id}
                            activeDirection={activeSort?.direction}
                            onChange={(id, direction) => {
                                const field = DisplaySortFields.find((f) => f.id === id);
                                if (!field) return;
                                const value = direction === 'desc' && field.desc ? field.desc : field.asc;
                                setSortBy(DisplaySortOptions.find((option) => option.value === value) ?? null);
                            }}
                            onClear={() => setSortBy(defaultSortBy ?? null)}
                        />

                        <button
                            type="button"
                            className={clsx(filterChipClass(), 'w-full justify-between md:hidden')}
                            onClick={() => setOpen(!open)}
                        >
                            {i18next.t('header:search-filters.title')}{filtersCounter > 0 ? ` (${filtersCounter})` : ''}
                            <AlignCenter />
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

const FilterSection = (props: { section: FilterEntity, isMobile?: boolean }) => {
    const { section, isMobile } = props;
    const [query, setQuery] = useState('');
    const listId = `filter-options-${section.id}`;
    const listRef = useRef<HTMLDivElement>(null);

    const filteredOptions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return section.options;
        // Match all space-separated tokens against label+value
        const tokens = q.split(/\s+/);
        return section.options.filter((o) => {
            const hay = `${o.label ?? ''} ${o.value ?? ''}`.toLowerCase();
            return tokens.every((t) => hay.includes(t));
        });
    }, [query, section.options]);

    const overflows = useOverflows(listRef, [filteredOptions.length]);

    return (
        <form className={clsx('space-y-[8px]', { 'w-[292px]': !isMobile })}>
            <div role="search">
                <label htmlFor={`search-${section.id}`} className="sr-only">
                    Search {section.name ?? section.id}
                </label>
                <SearchInput
                    id={`search-${section.id}`}
                    type="search"
                    inputMode="search"
                    autoComplete="off"
                    placeholder={i18next.t('header:search-filters.search-placeholder')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-controls={listId}
                    fluid
                />
            </div>
            {/*
              * The only scroller: the panel above it does not scroll, so the
              * search field stays put while the rows move.
              *
              * 312px and the bottom fade are the comp's (master 396:7113,
              * fade 396:7162 — 71.5px, BG/100 to transparent). It was 30vh,
              * which made the panel's height depend on the window rather
              * than the design, and cut off with a hard edge.
              *
              * The fade is pointer-events-none, or it would swallow clicks on
              * the last two rows.
              */}
            <div className="relative">
                <div ref={listRef} id={listId} className={clsx({ 'ds-scrollbar max-h-[312px] overflow-y-auto overscroll-contain': !isMobile })}>
                    {filteredOptions.length === 0 ? (
                        <p className="text-body-400 px-[12px] py-[8px] text-ink-300">
                            {i18next.t('header:search-filters.no-matches', 'No matches.')}
                        </p>
                    ) : (filteredOptions.map((option) => (
                        <SelectionRow
                            key={option.value}
                            label={option.label}
                            selected={option.selected}
                            disabled={option.disabled}
                            onChange={(next) => option.onChange(next)}
                        />
                    )))}
                </div>
                {!isMobile && (
                    <div
                        aria-hidden
                        className={clsx(
                            'pointer-events-none absolute inset-x-0 bottom-0 h-[71px] bg-gradient-to-b from-transparent to-surface-100',
                            'duration-dropdown ease-ds-out transition-opacity',
                            overflows ? 'opacity-100' : 'opacity-0'
                        )}
                    />
                )}
            </div>
        </form>
    );
};

export default SearchFilters;

/* eslint-enable @typescript-eslint/no-use-before-define */
